import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_TEAMS } from '../../graphql/teams';
import { 
  sortTeams, 
  getTeamColor, 
  getTeamDisplayName,
  TEAM_DEPARTMENT_OPTIONS,
  getTeamDepartmentLabel,
  getAssignedTeamMembers,
  getTeamMemberDisplayName
} from './teams-constants';
import { devError } from './constants';
import CreateTeamModal from './CreateTeamModal';
import EditTeamModal from './EditTeamModal';

export default function TeamsTab({ canManageTeamMembers = false }) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // ALL, ACTIVE, INACTIVE
  const [departmentFilter, setDepartmentFilter] = useState('ALL');

  const { data: teamsData, loading: isLoadingTeams, refetch: refetchTeams, error } = useQuery(GET_TEAMS, {
    fetchPolicy: 'network-only',
    onError: (error) => {
      devError('Error fetching teams:', error);
    }
  });

  // Filter and sort teams
  const filteredTeams = React.useMemo(() => {
    if (!teamsData?.teams) return [];
    
    let filtered = [...teamsData.teams];
    
    // Filter by status
    if (statusFilter === 'ACTIVE') {
      filtered = filtered.filter(team => team.isActive);
    } else if (statusFilter === 'INACTIVE') {
      filtered = filtered.filter(team => !team.isActive);
    }
    
    if (departmentFilter !== 'ALL') {
      filtered = filtered.filter(team => team.department === departmentFilter);
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(team => 
        team.name.toLowerCase().includes(searchLower) ||
        (team.description && team.description.toLowerCase().includes(searchLower))
      );
    }
    
    return sortTeams(filtered);
  }, [teamsData?.teams, statusFilter, departmentFilter, searchTerm]);

  const handleEditTeam = (team) => {
    if (!canManageTeamMembers) return;
    setSelectedTeam(team);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedTeam(null);
  };

  if (isLoadingTeams) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-300 sm:text-gray-500">Loading teams...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-400 sm:text-red-600">Error loading teams: {error.message}</div>
        <button 
          onClick={() => refetchTeams()}
          className="mt-2 text-sky-400 sm:text-sky-600 hover:text-sky-300 sm:hover:text-sky-700"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-white sm:text-gray-900">Teams Management</h2>
          <p className="text-sm text-gray-300 sm:text-gray-600">Organize your team members into groups</p>
        </div>
        {canManageTeamMembers && (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-sky-600 text-white px-4 py-2 rounded-lg hover:bg-sky-700 flex items-center justify-center gap-2 text-sm"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span className="hidden sm:inline">Create Team</span>
            <span className="sm:hidden">Create</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search teams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-sky-500 focus:border-sky-500"
          />
        </div>
        
        <div className="sm:w-48">
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-sky-500 focus:border-sky-500"
          >
            <option value="ALL">All departments</option>
            {TEAM_DEPARTMENT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        
        {/* Status Filter */}
        <div className="sm:w-48">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-sky-500 focus:border-sky-500"
          >
            <option value="ALL">All Teams</option>
            <option value="ACTIVE">Active Teams</option>
            <option value="INACTIVE">Inactive Teams</option>
          </select>
        </div>
      </div>

      {/* Teams Grid */}
      {filteredTeams.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-white sm:text-gray-900">No teams found</h3>
          <p className="mt-1 text-sm text-gray-300 sm:text-gray-500">
            {searchTerm || statusFilter !== 'ALL' 
              ? 'Try adjusting your search or filters.'
              : 'Get started by creating your first team.'
            }
          </p>
          {canManageTeamMembers && !searchTerm && statusFilter === 'ALL' && (
            <div className="mt-6">
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-sky-600 hover:bg-sky-700"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create Team
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTeams.map((team) => {
            const assignedMembers = getAssignedTeamMembers(team);
            return (
            <div
              key={team.id}
              className={`bg-white border border-gray-200 rounded-lg p-6 transition-shadow ${
                canManageTeamMembers ? 'hover:shadow-md cursor-pointer' : ''
              }`}
              onClick={() => handleEditTeam(team)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: getTeamColor(team) }}
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {getTeamDisplayName(team)}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {getTeamDepartmentLabel(team.department)}
                    </p>
                    {team.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                        {team.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      team.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {team.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              
              <div className="mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {assignedMembers.length} member{assignedMembers.length !== 1 ? 's' : ''}
                  </div>
                  {canManageTeamMembers && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditTeam(team);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                      aria-label={`Edit ${getTeamDisplayName(team)}`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {assignedMembers.length > 0 ? (
                    assignedMembers.map((member) => (
                      <span
                        key={member.id || getTeamMemberDisplayName(member)}
                        className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700"
                      >
                        {getTeamMemberDisplayName(member)}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-gray-500">No members assigned</span>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Summary */}
      {filteredTeams.length > 0 && (
        <div className="mt-6 text-sm text-gray-300 sm:text-gray-500 text-center">
          Showing {filteredTeams.length} of {teamsData?.teams?.length || 0} teams
        </div>
      )}

      {/* Modals */}
      <CreateTeamModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />

      <EditTeamModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        team={selectedTeam}
      />
    </div>
  );
}
